import { useState, useEffect, useRef, useCallback } from "react";
import { Phone, PhoneOff, Mic, MicOff, Lock, Volume2, VolumeX } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { api } from "@/App";
import { t } from "@/i18n";

const DEFAULT_ICE = [{ urls: "stun:stun.l.google.com:19302" }];

const fetchIceServers = async () => {
  try {
    const { data } = await api.get("/calls/ice-servers");
    return data.iceServers || DEFAULT_ICE;
  } catch {
    return DEFAULT_ICE;
  }
};

const waitIceComplete = (pc) =>
  new Promise((resolve) => {
    if (pc.iceGatheringState === "complete") return resolve();
    const timeout = setTimeout(resolve, 4000);
    pc.addEventListener("icegatheringstatechange", () => {
      if (pc.iceGatheringState === "complete") {
        clearTimeout(timeout);
        resolve();
      }
    });
  });

export const CallScreen = ({ mode, callId: incomingCallId, offer, otherUser, onClose }) => {
  const [status, setStatus] = useState(mode === "outgoing" ? "calling" : "connecting");
  const [muted, setMuted] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [seconds, setSeconds] = useState(0);
  const [failReason, setFailReason] = useState(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const callIdRef = useRef(incomingCallId || null);
  const closedRef = useRef(false);
  const didStartRef = useRef(false);
  const unmountTimerRef = useRef(null);

  const releaseMedia = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    try { pcRef.current?.close(); } catch {}
  };

  // Explicit hangup / terminal state
  const hangup = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    releaseMedia();
    if (callIdRef.current) api.post(`/calls/${callIdRef.current}/end`).catch(() => {});
    onClose();
  }, [onClose]);

  useEffect(() => {
    // StrictMode remount: cancel the pending real-unmount teardown
    if (unmountTimerRef.current) {
      clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }

    const createPC = async () => {
      const iceServers = await fetchIceServers();
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.error("getUserMedia failed", err);
        setStatus("mic-error");
        setTimeout(hangup, 3500);
        return null;
      }
      streamRef.current = stream;
      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.ontrack = (e) => {
        if (audioRef.current) {
          audioRef.current.srcObject = e.streams[0];
          audioRef.current.play().catch(() => {});
        }
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "connected") setStatus("active");
        if (["failed", "closed"].includes(pc.connectionState) && !closedRef.current) {
          setStatus("ended");
          setTimeout(hangup, 800);
        }
      };
      return pc;
    };

    const start = async () => {
      const pc = await createPC();
      if (!pc) return;
      try {
        if (mode === "outgoing") {
          const off = await pc.createOffer();
          await pc.setLocalDescription(off);
          await waitIceComplete(pc);
          const { data } = await api.post("/calls/start", {
            to_user_id: otherUser.user_id,
            offer: pc.localDescription.toJSON(),
          });
          callIdRef.current = data.call_id;
          setStatus("ringing");
        } else {
          await pc.setRemoteDescription(offer);
          const ans = await pc.createAnswer();
          await pc.setLocalDescription(ans);
          await waitIceComplete(pc);
          await api.post(`/calls/${callIdRef.current}/answer`, { answer: pc.localDescription.toJSON() });
        }
      } catch (e) {
        console.error("Call setup failed", e);
        setFailReason(e?.response?.data?.detail || null);
        setStatus("call-failed");
        setTimeout(hangup, 3000);
      }
    };

    if (!didStartRef.current) {
      didStartRef.current = true;
      start();
    }

    const pollInterval = setInterval(async () => {
      if (!callIdRef.current || closedRef.current) return;
      try {
        const { data } = await api.get(`/calls/${callIdRef.current}`);
        if (["declined", "ended", "missed"].includes(data.status)) {
          setStatus(data.status === "declined" ? "declined" : "ended");
          clearInterval(pollInterval);
          setTimeout(hangup, 1000);
        } else if (data.status === "accepted" && data.answer && pcRef.current && !pcRef.current.currentRemoteDescription) {
          await pcRef.current.setRemoteDescription(data.answer);
        }
      } catch {}
    }, 1500);

    return () => {
      clearInterval(pollInterval);
      // Deferred teardown: skipped if StrictMode remounts immediately
      unmountTimerRef.current = setTimeout(() => {
        if (closedRef.current) return;
        closedRef.current = true;
        releaseMedia();
        if (callIdRef.current) api.post(`/calls/${callIdRef.current}/end`).catch(() => {});
      }, 80);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== "active") return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [status]);

  const toggleMute = () => {
    const track = streamRef.current?.getAudioTracks()?.[0];
    if (track) {
      track.enabled = muted;
      setMuted(!muted);
    }
  };

  const toggleSpeaker = async () => {
    const next = !speakerOn;
    setSpeakerOn(next);
    // Native iOS: route audio via AVAudioSession (custom Capacitor plugin)
    try {
      const { Capacitor, registerPlugin } = await import("@capacitor/core");
      if (Capacitor.isNativePlatform()) {
        const Speaker = registerPlugin("AdeSpeaker");
        await Speaker.setSpeaker({ on: next });
        return;
      }
    } catch (e) { /* plugin not available */ }
    // Web fallback: switch audio output device when supported
    try {
      if (audioRef.current?.setSinkId && navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const outs = devices.filter((d) => d.kind === "audiooutput");
        const speaker = outs.find((d) => /speaker/i.test(d.label));
        const earpiece = outs.find((d) => /earpiece|receiver|handset/i.test(d.label));
        if (next && speaker) await audioRef.current.setSinkId(speaker.deviceId);
        else if (!next && earpiece) await audioRef.current.setSinkId(earpiece.deviceId);
      }
    } catch (e) { /* not supported */ }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const statusLabel = {
    calling: t("callStarting"),
    ringing: t("callRinging"),
    connecting: t("callConnecting"),
    active: fmt(seconds),
    declined: t("callDeclinedLog"),
    ended: t("callEndedLabel"),
    "mic-error": t("callMicError"),
    "call-failed": failReason || t("callFailed"),
  }[status];

  return (
    <div className="fixed inset-0 z-[100] bg-gray-950 flex flex-col items-center justify-between py-16 safe-bottom" data-testid="call-screen">
      <audio ref={audioRef} autoPlay playsInline />
      <div className="flex flex-col items-center gap-4 mt-8">
        <div className={`rounded-full p-1 ${status === "active" ? "bg-gradient-to-br from-emerald-400 to-teal-500" : "bg-gradient-to-br from-orange-500 via-pink-500 to-purple-500"} ${status === "ringing" || status === "calling" ? "animate-pulse" : ""}`}>
          <Avatar className="w-28 h-28 border-4 border-gray-950">
            {otherUser?.picture ? (
              <AvatarImage src={otherUser.picture.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${otherUser.picture}` : otherUser.picture} alt={otherUser?.name} />
            ) : (
              <AvatarFallback className="bg-gray-800 text-white text-3xl">{otherUser?.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
            )}
          </Avatar>
        </div>
        <h2 className="text-white text-2xl font-semibold" data-testid="call-other-name">{otherUser?.name}</h2>
        <p className="text-gray-400 text-sm" data-testid="call-status">{statusLabel}</p>
        {status === "mic-error" && (
          <p className="text-amber-400/90 text-xs text-center max-w-[280px]" data-testid="mic-error-hint">
            {t("callMicHint")}
          </p>
        )}
        <div className="flex items-center gap-1.5 text-emerald-500/80 text-[11px]">
          <Lock className="w-3 h-3" />
          {t("callE2E")}
        </div>
      </div>

      <div className="flex items-center gap-6">
        <button
          onClick={toggleMute}
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${muted ? "bg-white text-gray-900" : "bg-white/10 text-white"}`}
          data-testid="call-mute-btn"
        >
          {muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
        </button>
        <button
          onClick={hangup}
          className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center active:scale-95 transition-transform shadow-lg shadow-red-500/40"
          data-testid="call-end-btn"
        >
          <PhoneOff className="w-7 h-7 text-white" />
        </button>
        <button
          onClick={toggleSpeaker}
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${speakerOn ? "bg-white text-gray-900" : "bg-white/10 text-white"}`}
          data-testid="call-speaker-btn"
        >
          {speakerOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
        </button>
      </div>
    </div>
  );
};

export const IncomingCallOverlay = ({ call, onAccept, onDecline }) => {
  const user = call.from_user || {};
  return (
    <div className="fixed inset-0 z-[100] bg-gray-950/95 backdrop-blur-xl flex flex-col items-center justify-between py-20 safe-bottom" data-testid="incoming-call-overlay">
      <div className="flex flex-col items-center gap-4 mt-8">
        <div className="rounded-full p-1 bg-gradient-to-br from-orange-500 via-pink-500 to-purple-500 animate-pulse">
          <Avatar className="w-28 h-28 border-4 border-gray-950">
            {user.picture ? (
              <AvatarImage src={user.picture.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${user.picture}` : user.picture} alt={user.name} />
            ) : (
              <AvatarFallback className="bg-gray-800 text-white text-3xl">{user.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
            )}
          </Avatar>
        </div>
        <h2 className="text-white text-2xl font-semibold">{user.name}</h2>
        <p className="text-gray-400 text-sm">{t("callIncomingLabel")}</p>
      </div>
      <div className="flex items-center gap-16">
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={onDecline}
            className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center active:scale-95 transition-transform shadow-lg shadow-red-500/40"
            data-testid="incoming-decline-btn"
          >
            <PhoneOff className="w-7 h-7 text-white" />
          </button>
          <span className="text-gray-400 text-xs">{t("callDecline")}</span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={onAccept}
            className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center active:scale-95 transition-transform shadow-lg shadow-emerald-500/40"
            data-testid="incoming-accept-btn"
          >
            <Phone className="w-7 h-7 text-white animate-bounce" />
          </button>
          <span className="text-gray-400 text-xs">{t("callAccept")}</span>
        </div>
      </div>
    </div>
  );
};
